import { memo, type ReactNode, useMemo } from 'react';

export const NOTIFICATION_CLIPS_MAX_COUNT = 5;
export const UI2026_NOTIFICATION_CLIPS_MAX_COUNT = 3;

type ThumbnailOverflowStyle = 'overlay' | 'chip';

export type NotificationItemsThumbnailsProps<T extends { id: string }> = {
  maxCount?: number;
  overflowStyle?: ThumbnailOverflowStyle;
  renderItem: (item: T) => ReactNode;
  totalItemsCount: number;
  items: T[];
};

type GetVisibleThumbnailItemsParams<T> = {
  items: T[];
  maxCount: number;
  totalItemsCount: number;
};

const getVisibleThumbnailItems = <T,>({ items, maxCount, totalItemsCount }: GetVisibleThumbnailItemsParams<T>) => {
  if (totalItemsCount > maxCount) {
    return items.slice(0, maxCount);
  }

  return items;
};

const NotificationItemsThumbnailsComponent = <T extends { id: string }>({
  renderItem,
  totalItemsCount,
  maxCount = NOTIFICATION_CLIPS_MAX_COUNT,
  overflowStyle = 'overlay',
  items,
}: NotificationItemsThumbnailsProps<T>) => {
  const overflowCount = totalItemsCount - maxCount;

  const formattedItems = useMemo(
    () =>
      getVisibleThumbnailItems({
        items,
        maxCount,
        totalItemsCount,
      }),
    [items, maxCount, totalItemsCount],
  );

  return (
    <div className="flex gap-3 ui2026:gap-0 ui2026:-space-x-2.5">
      {formattedItems?.map((item, index) => {
        const isOverlayOverflowThumb = overflowStyle === 'overlay' && overflowCount > 0 && index === maxCount - 1;

        if (isOverlayOverflowThumb) {
          return (
            <div className="relative" key={item.id || index}>
              {renderItem(item)}
              <div className="absolute inset-0 flex items-center justify-center rounded-4 ads:bg-overlay/60 bg-pigeon-900/60 font-medium text-14 text-white">
                +{overflowCount}
              </div>
            </div>
          );
        }

        if (overflowStyle === 'chip') {
          return (
            <div
              className="relative shrink-0 overflow-hidden rounded-8 ads:ring-ads-gray-1 ring-1 ring-gray-1"
              key={item.id || index}
              style={{ zIndex: formattedItems.length - index }}
            >
              {renderItem(item)}
            </div>
          );
        }

        return renderItem(item);
      })}
      {overflowStyle === 'chip' && overflowCount > 0 && (
        <div
          className="relative z-0 flex size-10 shrink-0 items-center justify-center rounded-8 ads:bg-ads-gray-11 bg-gray-11 font-semibold ads:text-ads-gray-1 text-10 text-gray-1 ads:ring-ads-gray-1 ring-1 ring-gray-1"
          data-testid="NOTIFICATIONS_ITEM_THUMBNAILS_OVERFLOW"
        >
          +{overflowCount}
        </div>
      )}
    </div>
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const NotificationItemsThumbnails = memo(
  NotificationItemsThumbnailsComponent,
) as typeof NotificationItemsThumbnailsComponent & {
  displayName: string;
};

NotificationItemsThumbnails.displayName = 'NotificationItemsThumbnails';
